// LanguageFileManagementPage.tsx
import * as React from "react";
import {ChangeEvent, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {withAuthWrapperComponent} from "@/app/auth/WithAuthWrapperComponent";
import {AppFrame} from "@/app/AppFrame";
import {CircularProgress, Grid2 as Grid} from "@mui/material";
import {CardEx} from "@/lib/components/CardEx";
import {IconButtonEx} from "@/lib/components/buttons/IconButtonEx";
import {getI18n, useTranslation} from "react-i18next";
import {AdminI18N, CommonI18N} from "@/translations";
import {useSnackbarEx} from "@/lib/snackbarex";
import {useDialogController} from "@/lib/dialog-controller";
import ConfirmationDialog from "@/lib/components/ConfirmationDialog";
import {DataTable} from "@/lib/components/DataTable";
import {CellContext, ColumnDef} from "@tanstack/react-table";
import {I18NImportLogDTO, I18NLanguageStatusDTO, Language} from "@/api/data-contracts";
import {Download as DownloadIcon, Upload as UploadIcon} from "@mui/icons-material";
import {formatDateTime, trimOrNull} from "@/lib/utils";
import {triggerBlobDownload} from "@/lib/blob-download";
import {refreshLanguageBundle} from "@/app/locale/i18nBundle";
import {exportLanguage, getImportLog, getImportLogDetail, getLanguageStatuses, importLanguage} from "./i18nApi";
import I18nImportLogDialog from "./I18nImportLogDialog";
import {languageFileCodes, languageLabelKeys} from "./languageLabels";

/**
 * `DataTable` requires `TData extends { id?: number }` and reads `row.original.id` for its
 * row-identity/selection check (`selectedRowId === row.original.id`). `I18NLanguageStatusDTO`
 * declares no `id`, so without one every row would share `id === undefined` and match that check
 * (`undefined === undefined`) — rendering all rows as "selected" and defeating the striped/hover
 * styling. This alias adds an `id` that `loadLanguageStatuses` populates with a stable per-row
 * index so each row is distinct.
 */
type LanguageStatusRow = I18NLanguageStatusDTO & { id?: number };

const LanguageFileManagementPage = () => {
    const { t } = useTranslation();
    const { showMessage } = useSnackbarEx();
    const [DialogNode, showDialog] = useDialogController();
    const [languageStatuses, setLanguageStatuses] = useState<LanguageStatusRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [importLogs, setImportLogs] = useState<I18NImportLogDTO[]>([]);
    const [importLogsLoading, setImportLogsLoading] = useState(false);
    const [importLog, setImportLog] = useState<I18NImportLogDTO | null>(null);
    const [showImportLog, setShowImportLog] = useState(false);
    const locale = getI18n().language;
    const fileInputRef = useRef<HTMLInputElement>(null);
    const uploadLanguageRef = useRef<Language | null>(null);

    const loadLanguageStatuses = useCallback(async () => {
        setLoading(true);
        try {
            const statuses = await getLanguageStatuses();
            // Assign a stable per-row id so DataTable's selection check does not treat all
            // (id-less) rows as equal — see the LanguageStatusRow type comment.
            setLanguageStatuses(statuses.map((status, index) => ({ ...status, id: index })));
        } catch (error: unknown) {
            showMessage({
                summary: t(AdminI18N.languageFiles.message.loadFailed),
                error,
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    }, [showMessage, t]);

    const loadImportLogs = useCallback(async () => {
        setImportLogsLoading(true);
        try {
            const logs = await getImportLog();
            setImportLogs(logs);
        } catch (error: unknown) {
            showMessage({
                summary: t(AdminI18N.languageFiles.message.importLogListLoadFailed),
                error,
                severity: "error"
            });
        } finally {
            setImportLogsLoading(false);
        }
    }, [showMessage, t]);

    useEffect(() => {
        loadLanguageStatuses();
    }, [loadLanguageStatuses]);

    useEffect(() => {
        loadImportLogs();
    }, [loadImportLogs]);

    const handleDownload = useCallback(
        async (language: Language) => {
            try {
                const blob = await exportLanguage(language);
                triggerBlobDownload(blob, `sprachdatei_${languageFileCodes[language]}.xlsx`);
            } catch (error: unknown) {
                showMessage({
                    summary: t(AdminI18N.languageFiles.message.downloadFailed),
                    error,
                    severity: "error"
                });
            }
        },
        [showMessage, t]
    );

    const handleImportConfirmed = useCallback(
        async (language: Language, file: File) => {
            try {
                const log = await importLanguage(language, file);
                setImportLog(log);
                setShowImportLog(true);
                // A successful import replaces the stored texts of that language, but every already
                // loaded session — including this one — keeps serving the bundle it fetched at page
                // load. Without this refresh the operator would confirm "Erfolg" and still read the
                // previous texts until a manual browser reload. Only this session is refreshed;
                // other users' open sessions still pick the change up on their next page load.
                if (log.success) {
                    await refreshLanguageBundle(language);
                }
                await loadLanguageStatuses();
                await loadImportLogs();
            } catch (error: unknown) {
                showMessage({
                    summary: t(AdminI18N.languageFiles.message.uploadFailed),
                    error,
                    severity: "error"
                });
            }
        },
        [loadLanguageStatuses, loadImportLogs, showMessage, t]
    );

    const handleFileSelected = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            // Reset the input value so selecting the same file again still fires onChange.
            event.target.value = "";
            const language = uploadLanguageRef.current;
            if (!file || !language) {
                return;
            }
            showDialog((open, close) => (
                <ConfirmationDialog
                    open={open}
                    onClose={close}
                    title={t(AdminI18N.languageFiles.dialog.confirmTitle)}
                    message={t(AdminI18N.languageFiles.dialog.confirmMessage)}
                    confirmButtonText={t(CommonI18N.message.yes)}
                    cancelButtonText={t(CommonI18N.message.no)}
                    onConfirm={() => {
                        close();
                        return handleImportConfirmed(language, file);
                    }}
                />
            ));
        },
        [showDialog, t, handleImportConfirmed]
    );

    const handleUploadClick = useCallback((language: Language) => {
        uploadLanguageRef.current = language;
        fileInputRef.current?.click();
    }, []);

    const handleCloseImportLog = useCallback(() => {
        setShowImportLog(false);
    }, []);

    const handleViewImportLog = useCallback(
        async (log: I18NImportLogDTO) => {
            if (log.id == null) {
                return;
            }
            try {
                const detail = await getImportLogDetail(log.id);
                setImportLog(detail);
                setShowImportLog(true);
            } catch (error: unknown) {
                showMessage({
                    summary: t(AdminI18N.languageFiles.message.importLogDetailLoadFailed),
                    error,
                    severity: "error"
                });
            }
        },
        [showMessage, t]
    );

    const columns: ColumnDef<LanguageStatusRow, unknown>[] = useMemo(
        () => [
            {
                header: t(AdminI18N.languageFiles.column.language),
                accessorFn: (row: LanguageStatusRow) => (row.language ? t(languageLabelKeys[row.language]) : "-"),
                id: "language"
            },
            {
                header: t(AdminI18N.languageFiles.column.maintained),
                accessorFn: (row: LanguageStatusRow) =>
                    row.maintained
                        ? t(AdminI18N.languageFiles.message.maintained)
                        : t(AdminI18N.languageFiles.message.notMaintained),
                id: "maintained"
            },
            {
                header: t(AdminI18N.languageFiles.column.entryCount),
                accessorFn: (row: LanguageStatusRow) => row.entryCount ?? 0,
                id: "entryCount"
            },
            {
                header: t(AdminI18N.languageFiles.column.deviating),
                accessorFn: (row: LanguageStatusRow) => row.deviatingFromBaselineCount ?? 0,
                id: "deviating"
            },
            {
                header: t(AdminI18N.languageFiles.column.lastContentChange),
                accessorFn: (row: LanguageStatusRow) =>
                        row.lastContentChangeAt ? formatDateTime(new Date(row.lastContentChangeAt), locale) : "-",
                id: "lastContentChange"
            },
            {
                header: t(AdminI18N.languageFiles.column.serverVersion),
                accessorFn: (row: LanguageStatusRow) => trimOrNull(row.lastContentChangeServerVersion) ?? "-",
                id: "serverVersion"
            },
            {
                header: t(AdminI18N.languageFiles.column.lastImportDate),
                accessorFn: (row: LanguageStatusRow) =>
                    row.lastImport?.createdAt ? formatDateTime(new Date(row.lastImport.createdAt), locale) : "-",
                id: "lastImportDate"
            },
            {
                header: t(AdminI18N.languageFiles.column.lastImportUser),
                accessorFn: (row: LanguageStatusRow) =>
                    row.lastImport?.createdBy
                        ? `${row.lastImport.createdBy.firstName ?? ""} ${row.lastImport.createdBy.lastName ?? ""}`.trim()
                        : "-",
                id: "lastImportUser"
            },
            {
                header: t(AdminI18N.languageFiles.column.lastImportStatus),
                accessorFn: (row: LanguageStatusRow) => {
                    if (!row.lastImport) {
                        return t(AdminI18N.languageFiles.message.noImportYet);
                    }
                    return row.lastImport.success
                        ? t(AdminI18N.languageFiles.message.importSuccess)
                        : t(AdminI18N.languageFiles.message.importFailed);
                },
                id: "lastImportStatus"
            },
            {
                header: "",
                id: "actions",
                // Action buttons are icon-only; suppress DataTable's per-cell tooltip so it does
                // not render the buttons a second time inside a hover tooltip.
                meta: { disableTooltip: true },
                cell: ({ row }: CellContext<LanguageStatusRow, unknown>) => {
                    const language = row.original.language;
                    if (!language) {
                        return null;
                    }
                    return (
                        <Grid container direction="row" spacing={0.5}>
                            <Grid>
                                <IconButtonEx
                                    icon={<DownloadIcon />}
                                    tooltip={t(AdminI18N.languageFiles.action.download)}
                                    onClick={() => handleDownload(language)}
                                    data-cy={`download-language-button-${language}`}
                                />
                            </Grid>
                            <Grid>
                                <IconButtonEx
                                    icon={<UploadIcon />}
                                    tooltip={t(AdminI18N.languageFiles.action.upload)}
                                    onClick={() => handleUploadClick(language)}
                                    data-cy={`upload-language-button-${language}`}
                                />
                            </Grid>
                        </Grid>
                    );
                }
            }
        ],
        [t, locale, handleDownload, handleUploadClick]
    );

    const importLogColumns: ColumnDef<I18NImportLogDTO, unknown>[] = useMemo(
        () => [
            {
                header: t(AdminI18N.languageFiles.protocolColumn.date),
                accessorFn: (row: I18NImportLogDTO) =>
                    row.createdAt ? formatDateTime(new Date(row.createdAt), locale) : "-",
                id: "date"
            },
            {
                header: t(AdminI18N.languageFiles.column.language),
                accessorFn: (row: I18NImportLogDTO) => (row.language ? t(languageLabelKeys[row.language]) : "-"),
                id: "language"
            },
            {
                header: t(AdminI18N.languageFiles.column.lastImportUser),
                accessorFn: (row: I18NImportLogDTO) =>
                    row.createdBy ? `${row.createdBy.firstName ?? ""} ${row.createdBy.lastName ?? ""}`.trim() : "-",
                id: "user"
            },
            {
                header: t(AdminI18N.languageFiles.column.entryCount),
                accessorFn: (row: I18NImportLogDTO) => row.entryCount ?? 0,
                id: "entryCount"
            },
            {
                header: t(AdminI18N.languageFiles.dialog.warningCount),
                accessorFn: (row: I18NImportLogDTO) => row.warningCount ?? 0,
                id: "warningCount"
            },
            {
                header: t(AdminI18N.languageFiles.column.lastImportStatus),
                accessorFn: (row: I18NImportLogDTO) =>
                    row.success
                        ? t(AdminI18N.languageFiles.message.importSuccess)
                        : t(AdminI18N.languageFiles.message.importFailed),
                id: "status"
            }
        ],
        [t, locale]
    );

    return (
        <AppFrame title={t(AdminI18N.languageFiles.page.title)}>
            <Grid container spacing={2} paddingTop={1}>
                <Grid size={{ xs: 12 }}>
                    <CardEx title={t(AdminI18N.languageFiles.page.cardTitle)}>
                        <Grid container spacing={2} direction="column" width="100%">
                            <Grid size={{ xs: 12 }}>
                                {loading ? (
                                    <CircularProgress />
                                ) : (
                                    <DataTable
                                        columns={columns}
                                        data={languageStatuses}
                                        striped
                                        noResultsValue={t(CommonI18N.message.notGiven)}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </CardEx>
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <CardEx title={t(AdminI18N.languageFiles.page.protocolCardTitle)}>
                        <Grid container spacing={2} direction="column" width="100%">
                            <Grid size={{ xs: 12 }}>
                                {importLogsLoading ? (
                                    <CircularProgress />
                                ) : (
                                    <DataTable
                                        columns={importLogColumns}
                                        data={importLogs}
                                        striped
                                        onRowClick={handleViewImportLog}
                                        noResultsValue={t(AdminI18N.languageFiles.message.noImportLogsFound)}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </CardEx>
                </Grid>
            </Grid>

            <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                style={{ display: "none" }}
                onChange={handleFileSelected}
                data-cy="language-file-input"
            />

            {importLog && (
                <I18nImportLogDialog open={showImportLog} onClose={handleCloseImportLog} importLog={importLog} />
            )}

            <DialogNode />
        </AppFrame>
    );
};

export default withAuthWrapperComponent(LanguageFileManagementPage);
